import { useQuery } from '@tanstack/react-query';
import { Trophy } from 'lucide-react';
import type { TodayAnniversary } from '@pms/shared';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { dashboardService } from '@/services/dashboard.service';

function getInitials(first: string, last: string) {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

function AnniversaryPersonCard({ person }: { person: TodayAnniversary }) {
  const fullName = `${person.first_name} ${person.last_name}`;
  const meta = [person.designation_name, person.department_name].filter(Boolean).join(' · ');
  const yearsLabel = `${person.years_of_service} Year${person.years_of_service === 1 ? '' : 's'}`;

  return (
    <div className="flex items-start gap-3 rounded-xl border border-yellow-100 bg-white/80 p-4 shadow-sm backdrop-blur-sm dark:border-yellow-800/30 dark:bg-gray-800/60">
      <div className="relative shrink-0">
        <div className="h-14 w-14 overflow-hidden rounded-full ring-2 ring-yellow-400 ring-offset-1 dark:ring-yellow-500 dark:ring-offset-gray-800">
          {person.avatar_url ? (
            <img
              src={person.avatar_url}
              alt={fullName}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-yellow-100 text-base font-bold text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300">
              {getInitials(person.first_name, person.last_name)}
            </div>
          )}
        </div>
        <span className="absolute -bottom-1 -right-1 text-base leading-none" role="img" aria-label="trophy">
          🏆
        </span>
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{fullName}</p>
        <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
          {person.employee_code && (
            <span className="inline-block rounded-full border border-yellow-300 bg-yellow-50 px-2 py-0.5 text-xs font-medium text-yellow-700 dark:border-yellow-700/50 dark:bg-yellow-900/30 dark:text-yellow-300">
              {person.employee_code}
            </span>
          )}
          <span className="inline-block rounded-full bg-yellow-500 px-2 py-0.5 text-xs font-bold text-white dark:bg-yellow-600">
            🎉 {yearsLabel} with us!
          </span>
        </div>
        {meta && (
          <p className="mt-1 truncate text-xs text-muted-foreground">{meta}</p>
        )}
        <p className="mt-1.5 text-xs italic text-yellow-700 dark:text-yellow-400">
          Congratulations on your {yearsLabel} work anniversary! Thank you for your dedication, hard work, and contribution to our team. Here's to many more years of growth and success together! 🌟
        </p>
      </div>
    </div>
  );
}

export function AnniversaryWidget() {
  const q = useQuery({
    queryKey: ['dashboard', 'anniversaries', 'today'],
    queryFn: () => dashboardService.getTodayAnniversaries(),
  });

  if (q.isLoading || !q.data?.length) return null;

  return (
    <Card className="border-yellow-200 bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50 shadow-card dark:border-yellow-900/40 dark:from-yellow-950/30 dark:via-amber-950/20 dark:to-orange-950/20">
      <CardHeader className="border-b border-yellow-200/60 px-4 py-2 dark:border-yellow-800/30">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold text-yellow-800 dark:text-yellow-300">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-yellow-100 text-yellow-700 [&>svg]:h-3.5 [&>svg]:w-3.5 dark:bg-yellow-900/50 dark:text-yellow-400">
            <Trophy />
          </span>
          Work Anniversaries Today
          <span className="ml-1 rounded-full bg-yellow-200 px-2 py-0.5 text-xs font-semibold text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300">
            {q.data.length}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        <div
          className={[
            'grid gap-3',
            q.data.length === 1
              ? 'grid-cols-1'
              : q.data.length === 2
                ? 'grid-cols-1 sm:grid-cols-2'
                : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
          ].join(' ')}
        >
          {q.data.map((person) => (
            <AnniversaryPersonCard key={person.id} person={person} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
